/**
 * Sorting for the blog list section.
 *
 * Unlike collections, the blog object has no server side sort_by: Liquid always
 * hands out the articles newest first, and Shopify cannot paginate a re-sorted
 * array. The list is therefore rendered server side in its default order, and
 * this file reorders it on top of that:
 *
 *   1. The chosen order is kept in the "sort_by" query parameter, so it survives
 *      pagination, reloads and sharing.
 *   2. The full article list comes from templates/blog.data.liquid. Liquid caps
 *      blog.articles at 50 per render, so its pages are walked until the whole
 *      blog is covered, and the result is cached for the session. The index is
 *      read from the path of the current page, which keeps it scoped to the tag
 *      that is being filtered on, and its page count comes from the first
 *      response, since the article count of the section is not tag scoped.
 *   3. The cards of the current page are replaced with the matching slice of the
 *      sorted list. The number of pages never changes, so the server rendered
 *      pagination stays correct and only needs the parameter added to its links.
 *   4. Any leading cards that came from the locale blog are kept in front of
 *      that slice. They are not in the index, which covers the listed blog
 *      alone, so replacing the grid outright would drop them.
 *
 * Without JavaScript the dropdown is hidden and the default order is served.
 */
const DEFAULT_SORT = 'date-descending';

/* Date only: an alphabetical order carries no meaning for a reader browsing a
   blog. A "sort_by" left over from that order is unknown here, so sortFromUrl
   falls back to the default rather than honouring it. */
const SORT_OPTIONS = [DEFAULT_SORT, 'date-ascending'];

/* Kept below the per host connection limit of the browsers. */
const MAX_PARALLEL_REQUESTS = 6;

/* One extra attempt covers a page that failed for a transient reason. */
const REQUEST_ATTEMPTS = 2;

/*
 * The index carries the rendered card of every article, about 2.3 KB each, and
 * the session storage it would go into is a few megabytes shared by the whole
 * origin. Filling it does not only cost this cache: it makes the writes of every
 * other feature throw. So a blog whose index does not comfortably fit is left
 * uncached and fetched again when it is needed — roughly 220 articles at this
 * size.
 */
const MAX_CACHE_BYTES = 512 * 1024;

class BlogList extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.grid = this.querySelector('.blog-list__grid');
    this.details = this.querySelector('.facet-filters__sort');

    if (!this.grid) return;

    this.articlesCount = parseInt(this.dataset.articlesCount, 10) || 0;
    this.perPage = parseInt(this.dataset.perPage, 10) || 0;
    this.page = parseInt(this.dataset.currentPage, 10) || 1;
    this.sort = BlogList.sortFromUrl();
    this.initialized = true;

    /*
     * The leading cards of the grid belong to the locale blog rather than to the
     * one being listed, so they are not part of the index that render() slices.
     * They are taken now, while the grid still holds what the server rendered,
     * and put back in front of every slice.
     */
    const localeCount = parseInt(this.dataset.localeCount, 10) || 0;
    this.localeHtml = Array.from(this.grid.children)
      .slice(0, localeCount)
      .map((card) => card.outerHTML)
      .join('');

    this.setUpDropdown();

    if (this.sort === DEFAULT_SORT) {
      this.classList.remove('blog-list--pending');
      return;
    }

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keyup', this.onKeyup);
  }

  /*
   * The dropdown is the disclosure of the collection pages, but without the
   * facet form behind it: the chosen radio drives the query parameter, and the
   * details element is closed by hand, since nothing else does it.
   */
  setUpDropdown() {
    if (!this.details) return;

    this.summaryValue = this.details.querySelector('.facets__summary-value');
    this.summary = this.details.querySelector('summary');
    this.radios = Array.from(this.details.querySelectorAll('input[name="sort_by"]'));

    this.showSort(this.sort);
    this.radios.forEach((radio) => radio.addEventListener('change', this.onSortChange.bind(this)));

    this.details.addEventListener('toggle', () => {
      if (this.summary) this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
    });

    this.onDocumentClick = (event) => {
      if (this.details.open && !this.details.contains(event.target)) this.details.open = false;
    };

    this.onKeyup = (event) => {
      if (event.key !== 'Escape' || !this.details.open) return;

      this.details.open = false;
      if (this.summary) this.summary.focus();
    };

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keyup', this.onKeyup);
  }

  /* Checks the matching radio and shows its label as the dropdown value. */
  showSort(sort) {
    const radio = (this.radios || []).find((option) => option.value === sort);

    if (!radio) return;

    radio.checked = true;

    const label = radio.parentElement.querySelector('.label');

    if (this.summaryValue && label) this.summaryValue.textContent = label.textContent.trim();
  }

  static sortFromUrl() {
    const sort = new URLSearchParams(window.location.search).get('sort_by');
    return SORT_OPTIONS.indexOf(sort) === -1 ? DEFAULT_SORT : sort;
  }

  /* A new order starts over from the first page. */
  onSortChange(event) {
    const url = new URL(window.location.href);
    const sort = event.target.value;

    url.searchParams.delete('page');

    if (sort === DEFAULT_SORT) {
      url.searchParams.delete('sort_by');
    } else {
      url.searchParams.set('sort_by', sort);
    }

    window.location.assign(url.toString());
  }

  async render() {
    try {
      const articles = await this.articleIndex();
      const start = (this.page - 1) * this.perPage;
      const pageArticles = articles.slice(start, start + this.perPage);

      if (this.perPage > 0 && pageArticles.length > 0) {
        this.grid.innerHTML = this.localeHtml + pageArticles.map((article) => article.html).join('');
        this.updatePaginationLinks();
      }
    } catch (error) {
      /* Keep the order that was rendered server side. */
      this.sort = DEFAULT_SORT;
      this.showSort(DEFAULT_SORT);
    } finally {
      this.classList.remove('blog-list--pending');
    }
  }

  sortArticles(articles) {
    if (this.sort === 'date-ascending') {
      return articles.sort((a, b) => a.published_at - b.published_at);
    }

    return articles.sort((a, b) => b.published_at - a.published_at);
  }

  async articleIndex() {
    const indexUrl = BlogList.indexUrl();
    const cacheKey = `blog-list:${indexUrl.pathname}:${this.articlesCount}`;
    const cached = BlogList.readCache(cacheKey);

    if (cached) return this.sortArticles(cached);

    const first = await BlogList.fetchPage(indexUrl, 1);
    const remaining = [];

    for (let page = 2; page <= (first.pages || 1); page += 1) remaining.push(page);

    const articles = (first.articles || [])
      .concat(await BlogList.fetchRemaining(indexUrl, remaining))
      .map((article) => ({ ...article, published_at: parseInt(article.published_at, 10) || 0 }));

    if (articles.length === 0) throw new Error('The blog article index is empty.');

    if (first.count > articles.length) throw new Error('The blog article index is incomplete.');

    BlogList.writeCache(cacheKey, articles);

    return this.sortArticles(articles);
  }

  static async fetchRemaining(indexUrl, pages) {
    const articles = [];

    for (let start = 0; start < pages.length; start += MAX_PARALLEL_REQUESTS) {
      const batch = pages.slice(start, start + MAX_PARALLEL_REQUESTS);
      /* eslint-disable-next-line no-await-in-loop -- one batch at a time is the point. */
      const responses = await Promise.all(batch.map((page) => BlogList.fetchPage(indexUrl, page)));

      responses.forEach((response) => articles.push(...(response.articles || [])));
    }

    return articles;
  }

  static indexUrl() {
    const url = new URL(window.location.href);

    url.searchParams.delete('sort_by');
    url.searchParams.delete('page');
    url.searchParams.set('view', 'data');

    return url;
  }

  static async fetchPage(indexUrl, page, attempt = 1) {
    const url = new URL(indexUrl);

    url.searchParams.set('page', page);

    try {
      const response = await fetch(url.toString());

      if (!response.ok) {
        const error = new Error(`Could not load the blog article index: ${response.status}`);
        error.status = response.status;
        throw error;
      }

      return await response.json();
    } catch (error) {
      /* A page that is missing stays missing; only transient faults are retried. */
      const permanent = error.status >= 400 && error.status < 500;

      if (permanent || attempt >= REQUEST_ATTEMPTS) throw error;

      return BlogList.fetchPage(indexUrl, page, attempt + 1);
    }
  }

  static readCache(key) {
    try {
      const cached = sessionStorage.getItem(key);
      const articles = cached ? JSON.parse(cached) : null;

      return Array.isArray(articles) && articles.length > 0 ? articles : null;
    } catch (error) {
      return null;
    }
  }

  static writeCache(key, articles) {
    try {
      const payload = JSON.stringify(articles);

      if (payload.length > MAX_CACHE_BYTES) return;

      sessionStorage.setItem(key, payload);
    } catch (error) {
      /* A full or unavailable storage only costs the cache. */
    }
  }

  updatePaginationLinks() {
    this.querySelectorAll('a.pagination__item').forEach((link) => {
      const url = new URL(link.href, window.location.origin);

      if (this.sort === DEFAULT_SORT) {
        url.searchParams.delete('sort_by');
      } else {
        url.searchParams.set('sort_by', this.sort);
      }

      link.href = url.toString();
    });
  }
}

if (!customElements.get('blog-list')) {
  customElements.define('blog-list', BlogList);
}
